import SvgIcon from "@/components/common/SvgIcon";
import { Typography } from "@/components/kit";
import useElementAtEnd from "@/hooks/general/useElementAtEnd";
import { useOnClickOutside } from "@/hooks/general/useOnClickOutside";
import { Combobox, Transition } from "@headlessui/react";
import { Spinner } from "@material-tailwind/react";
import classNames from "classnames";
import {
  FocusEvent,
  Fragment,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

export type TAutoCompleteProps<T> = {
  query: string;
  setQuery: (str: string) => void;
  options: T[];
  setValue: (data: T) => void;
  value: T | undefined;
  renderOption?: (data: T) => ReactNode;
  renderValue?: (data: T) => ReactNode;
  placeholder?: string;
  error?: string | boolean;
  onOptionHover?: string;
  isLoading?: boolean;
  noItem?: ReactNode;
  startAdornment?: ReactNode;
  disabled?: boolean;
  clearable?: boolean;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  className?: string;
  dropdownClassName?: string;
  name?: string;
  closeAfterSelect?: boolean;
  textboxClassName?: string;
  isCalculatedTop?: boolean;
  startAdornmentClassName?: string;
};

const AutoComplete = <T,>(props: TAutoCompleteProps<T>) => {
  const { t } = useTranslation("common");
  const {
    options,
    closeAfterSelect = true,
    query = "",
    setQuery,
    value,
    renderValue,
    setValue,
    renderOption,
    placeholder,
    error,
    onOptionHover = "",
    isLoading,
    noItem = t("noResults"),
    startAdornment,
    startAdornmentClassName,
    disabled = false,
    clearable,
    onBlur,
    className,
    dropdownClassName,
    textboxClassName,
    isCalculatedTop = true,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [isFocus, setIsFocus] = useState<boolean>(false);
  const { elementRef, isAtEnd } = useElementAtEnd();
  const height = options.length * 40;
  const calculatedTop = isAtEnd
    ? `-${isLoading ? 80 : height > 250 ? 250 : height}px`
    : "";
  const inputRef = useRef<any>(null);
  const wrapperRef = useRef<any>(null);

  const activeFocus = () => {
    if (disabled) return;
    inputRef.current.focus();
    setIsFocus(true);
    setIsOpen((p) => !p);
  };

  useEffect(() => {
    setIsOpen(!!query);
  }, [query]);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
    }
  }, [isOpen]);
  const deActivate = () => {
    setTimeout(() => {
      if (inputRef.current) inputRef.current.blur();
      setIsFocus(false);
    });
  };

  const onChange = (value: any) => {
    deActivate();
    setValue(value);
  };

  useEffect(() => {
    const clickHandler = (e: any) => {
      if (!wrapperRef.current?.contains(e.target)) {
        deActivate();
      }
    };
    document.body.addEventListener("click", clickHandler, true);
    return document.body.removeEventListener("click", clickHandler);
  }, []);

  const clear = () => {
    setQuery("");
    onChange({} as T);
  };

  const hasValue =
    typeof value === "object" && value && !!Object.keys(value).length;

  const optionsContainer = useRef<HTMLDivElement>(null);

  useOnClickOutside(wrapperRef, () => {
    setIsOpen(false);
  });

  return (
    <div className="w-full" ref={wrapperRef}>
      <div ref={elementRef} className={classNames("h-[56px]", className)}>
        <Combobox disabled={disabled} value={value} onChange={onChange}>
          <div className="relative h-full">
            <div
              onClick={activeFocus}
              className={classNames(
                "flex relative w-full h-full cursor-default overflow-hidden rounded-lg text-left border border-1 border-gray-200 focus-visible:bg-red-400 sm:text-sm",
                {
                  "border-2 border-red-600": !!error,
                },
                textboxClassName,
              )}
            >
              {startAdornment && <div className="w-8"></div>}
              {!isFocus && hasValue && (
                <div className="flex-grow flex items-center px-4 w-[75%] truncate ">
                  {renderValue?.(value) || "---"}
                </div>
              )}

              <Combobox.Input
                ref={inputRef}
                placeholder={!hasValue ? placeholder : ""}
                className={classNames(
                  "w-full bg-inherit !outline-0 py-2 text-[16px] leading-5 text-gray-900 placeholder:text-gray-400 flex-1 px-4",
                )}
                displayValue={() => ""}
                onChange={(event) => setQuery(event.target.value)}
                onBlur={onBlur}
                autoComplete=""
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen((p) => !p);
                }}
              />

              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                {() => {
                  return (
                    <>
                      {clearable && hasValue && (
                        <SvgIcon
                          onClick={() => {
                            clear();
                          }}
                          className={classNames(
                            "cursor-pointer fill-gray-800 icon w-[20px] h-[20px] mr-2",
                            {
                              invisible: isLoading,
                              "transition-all": !isLoading,
                            },
                          )}
                          iconName={"close"}
                        />
                      )}

                      <SvgIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsOpen((p) => !p);
                        }}
                        iconName="arrowDown"
                        className={classNames(
                          "fill-gray-800 icon w-3 h-3 mr-2 transition-all",
                          {
                            "rotate-180": isOpen,
                          },
                        )}
                      />
                    </>
                  );
                }}
              </Combobox.Button>

              {!!startAdornment && (
                <div
                  className={classNames(
                    `absolute inset-y-0 left-3 flex justify-center items-center ${startAdornmentClassName}`,
                    {
                      "border-gray-200 shadow-none opacity-30": disabled,
                    },
                  )}
                >
                  {startAdornment}
                </div>
              )}
            </div>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              // afterLeave={() => setQuery("")}
              show={isOpen}
              ref={optionsContainer}
            >
              <Combobox.Options
                className={classNames(
                  "absolute mt-1   max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-blue-gray-800 py-1 text-base shadow-[0px_1px_8px_0px_rgba(0,0,0,0.08)] ring-1 ring-black/5 sm:text-sm z-10",
                  dropdownClassName,
                )}
                style={{
                  top: isCalculatedTop ? calculatedTop : undefined,
                }}
              >
                {(isLoading && (
                  <div className={"flex justify-center items-center p-4"}>
                    <Spinner className={"w-8 h-8"} />
                  </div>
                )) || (
                  <>
                    {(!options?.length && (
                      <div className="relative cursor-default select-none text-gray-700 p-3">
                        {noItem}
                      </div>
                    )) ||
                      options?.map((option, i) => (
                        <Combobox.Option
                          key={i}
                          className={({ active }) =>
                            `relative cursor-pointer select-none ${
                              active
                                ? `bg-indigo-500 text-white ${onOptionHover}`
                                : "text-gray-900"
                            }`
                          }
                          value={option}
                        >
                          {() => (
                            <div
                              className="py-2 ps-4 pr-4"
                              onClick={() => {
                                if (closeAfterSelect) {
                                  setIsOpen(false);
                                  return;
                                }
                              }}
                            >
                              {renderOption?.(option)}
                            </div>
                          )}
                        </Combobox.Option>
                      ))}
                  </>
                )}
              </Combobox.Options>
            </Transition>
          </div>
        </Combobox>
      </div>
      {!!error && (
        <div className="error ps-2 pt-2 flex justify-start items-center gap-1">
          <SvgIcon
            className={"w-3 h-3"}
            fill="fill-red-600"
            iconName="attentionRed"
          />
          <Typography color={"text-red-600"} variant={"Caption1-Regular"}>
            {error}
          </Typography>
        </div>
      )}
    </div>
  );
};
export default AutoComplete;
